// custom-select.js
//
// Progressive enhancement for a native <select> whose options carry several
// values (5 by default). The native element stays in the DOM and keeps the
// value, so normal form POSTs, Django form validation and JS that reads
// `select.value` keep working -- only the presentation is replaced.
//
// The dropdown is rendered as a real <table>. A table has one column model for
// all of its rows, so a column is exactly as wide in every option as it is in
// the header -- business values (dates, amounts, status) stay under their
// header instead of drifting apart as soon as one option is longer than the
// others. The closed button uses the same widths via CSS table display, so the
// selected row keeps the columns of the list it came from.
//
// Usage:
//
//   <select id="unit" name="unit" class="js-custom-select"
//           data-columns="1.4fr 1fr 1fr 6rem auto"    <!-- per column width -->
//           data-gap="1.5rem"                         <!-- space between columns -->
//           data-align="left left left right center"  <!-- per column alignment -->
//           data-headers="Unit|Model|Site|SoC|Status"
//           data-table-layout="fixed"                 <!-- fixed | auto -->
//           data-separator="|"
//           data-search="true"
//           data-placeholder="Select a unit"
//           data-button-class="...">                 <!-- replaces the default look -->
//     <option value="1042">V-1042|Movaria X3|Berlin|84 %|Active</option>
//   </select>
//
// data-columns takes one entry per column: a CSS length or percentage, or an
// `fr` share of whatever the fixed columns leave over, or `auto` to let the
// table size the column from its content. With a width on every column the
// layout is `fixed` (columns are exactly proportional, long values truncate),
// otherwise `auto` (columns grow to their widest value). data-table-layout
// forces one of the two.
//
// Per option the columns can also be given explicitly, which avoids escaping
// problems when a value contains the separator:
//
//   <option value="1042"
//           data-values='["V-1042","Movaria X3","Berlin","84 %","Active"]'
//           data-classes="font-medium||text-gray-500||text-emerald-600">
//   </option>
//
// `data-classes` is separator-delimited and adds classes to the matching
// column, e.g. to colour a status value.

const SELECTOR = '.js-custom-select';

const DEFAULTS = {
  separator: '|',
  columns: '',
  gap: '1rem',
  placeholder: 'Please select',
};

const ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

// Structural classes the button always needs for the caret/table layout.
const BUTTON_BASE =
  'flex w-full items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-60';

// Appearance only -- replaceable per instance via data-button-class so the
// button can be made to look like the plain <select> it replaces on a page.
const BUTTON_SKIN =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ' +
  'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ' +
  'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200';

const PANEL_CLASS =
  'absolute z-50 hidden w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg ' +
  'dark:border-gray-700 dark:bg-gray-800';

// border-separate keeps the sticky header border visible while rows scroll
// underneath it -- with border-collapse the browser drops it.
const TABLE_CLASS = 'w-full border-separate border-spacing-0 text-sm';

const HEAD_CELL_CLASS =
  'sticky top-0 z-10 border-b border-gray-200 bg-gray-50 py-2 text-xs font-semibold uppercase ' +
  'tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400';

const ROW_CLASS =
  'cursor-pointer text-gray-700 dark:text-gray-300 [content-visibility:auto] [contain-intrinsic-size:auto_2.25rem]';

const CELL_CLASS = 'py-2 align-middle';

// Inset of the first and last column, matching the px-3 of the panel chrome.
const EDGE_PADDING = '0.75rem';

const LENGTH = /^-?[\d.]+(?:px|rem|em|ch|%|vw|vh|vmin|vmax|pt|pc|cm|mm|in)$/;
const FRACTION = /^[\d.]+fr$/;

let uid = 0;

/** Split a separator-delimited string, tolerating undefined/empty input. */
function split(value, separator) {
  if (!value) return [];
  return value.split(separator).map((part) => part.trim());
}

/** Drop the float noise a share calculation leaves behind. */
function tidy(number) {
  return String(Number(number.toFixed(4)));
}

/**
 * Turn a grid-like column spec into one CSS width per column.
 *
 * Lengths and percentages are passed through, `fr` values share the space the
 * fixed columns leave over (expressed as calc so the browser does the math),
 * everything else -- `auto`, repeat()/minmax(), a spec with the wrong number of
 * entries -- becomes null, which leaves that column to the table layout.
 */
function parseWidths(spec, columnCount) {
  const widths = new Array(columnCount).fill(null);
  if (!spec || /repeat\(|minmax\(|calc\(/.test(spec)) return widths;

  const parts = spec.trim().split(/\s+/).filter(Boolean).slice(0, columnCount);
  const shares = parts.map((part) => (FRACTION.test(part) ? parseFloat(part) : null));
  const shareTotal = shares.reduce((sum, share) => sum + (share || 0), 0);

  const fixed = parts.filter((part, i) => shares[i] === null && LENGTH.test(part));
  const budget = fixed.length ? `(100% - (${fixed.join(' + ')}))` : null;

  parts.forEach((part, i) => {
    if (shares[i] !== null && shareTotal) {
      const share = shares[i] / shareTotal;
      widths[i] = budget ? `calc(${budget} * ${tidy(share)})` : `${tidy(share * 100)}%`;
    } else if (LENGTH.test(part)) {
      widths[i] = part;
    }
  });
  return widths;
}

/** Read the columns of one <option>, either from data-values or the label. */
function readValues(option, separator, columnCount) {
  let values;
  if (option.dataset.values) {
    try {
      values = JSON.parse(option.dataset.values);
    } catch (err) {
      console.warn('custom-select: invalid data-values on', option, err);
    }
  }
  if (!Array.isArray(values)) {
    values = split(option.textContent, separator);
  }
  // Pad so every option renders the same number of cells -- otherwise a short
  // option would leave the row with fewer columns than the header.
  while (values.length < columnCount) values.push('');
  return values.slice(0, columnCount);
}

class CustomSelect {
  constructor(select) {
    this.select = select;
    this.separator = select.dataset.separator || DEFAULTS.separator;
    this.columns = select.dataset.columns || DEFAULTS.columns;
    this.gap = select.dataset.gap || DEFAULTS.gap;
    this.placeholder = select.dataset.placeholder || DEFAULTS.placeholder;
    this.headers = split(select.dataset.headers, this.separator);
    this.withSearch = select.dataset.search === 'true';
    this.align = split(select.dataset.align, ' ');
    // The column count follows the template, so the same component also works
    // with 3 or 7 values -- 5 is only the default.
    this.columnCount =
      parseInt(select.dataset.columnCount, 10) ||
      this.headers.length ||
      this.align.length ||
      this.columns.trim().split(/\s+/).filter(Boolean).length ||
      5;

    this.widths = parseWidths(this.columns, this.columnCount);
    // Fixed layout needs a width on every column, otherwise the table has to
    // measure the content to place the columns.
    this.tableLayout =
      select.dataset.tableLayout || (this.widths.every(Boolean) ? 'fixed' : 'auto');

    this.id = select.id || `custom-select-${++uid}`;
    this.open = false;
    this.activeIndex = -1;
    this.lastFilter = null;
    this.rowsBuilt = false;
    this.typeahead = '';
    this.typeaheadTimer = null;

    this.build();
    this.bind();
    this.syncFromSelect();
  }

  /* ---------------------------------------------------------------- build */

  build() {
    const select = this.select;

    this.root = document.createElement('div');
    this.root.className = 'relative';
    select.parentNode.insertBefore(this.root, select);

    select.classList.add('sr-only');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    this.root.appendChild(select);

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = `${BUTTON_BASE} ${select.dataset.buttonClass || BUTTON_SKIN}`;
    this.button.id = `${this.id}-button`;
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.disabled = select.disabled;

    // A <button> may only contain phrasing content, so the selected row is laid
    // out with CSS table display instead of a real <table>.
    this.buttonRow = document.createElement('span');
    this.buttonRow.className = 'min-w-0 flex-1';

    this.button.appendChild(this.buttonRow);
    this.button.appendChild(this.caret());
    this.root.appendChild(this.button);

    this.panel = document.createElement('div');
    // Content sized columns may need more room than the button is wide; with
    // fixed widths the panel follows the button instead.
    this.panel.className =
      this.tableLayout === 'fixed' ? PANEL_CLASS : `${PANEL_CLASS} min-w-max`;

    if (this.withSearch) {
      const wrap = document.createElement('div');
      wrap.className = 'border-b border-gray-200 p-2 dark:border-gray-700';
      this.search = document.createElement('input');
      this.search.type = 'text';
      this.search.placeholder = 'Search…';
      this.search.className =
        'w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 ' +
        'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ' +
        'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200';
      wrap.appendChild(this.search);
      this.panel.appendChild(wrap);
    }

    // The header scrolls with the body so both stay part of the same table --
    // it is pinned with position: sticky rather than split into a second table.
    this.scroller = document.createElement('div');
    this.scroller.className = 'max-h-64 overflow-y-auto';

    this.table = document.createElement('table');
    this.table.className = TABLE_CLASS;
    this.table.style.tableLayout = this.tableLayout;
    // The rows are listbox options, not data rows -- keep the table itself out
    // of the accessibility tree.
    this.table.setAttribute('role', 'presentation');
    this.table.appendChild(this.colgroup());

    if (this.headers.length) {
      const head = document.createElement('thead');
      const row = document.createElement('tr');
      for (let i = 0; i < this.columnCount; i++) {
        const cell = document.createElement('th');
        cell.scope = 'col';
        cell.className = `${HEAD_CELL_CLASS} ${this.alignClass(i)} ${this.clipClass()}`;
        this.padCell(cell, i, EDGE_PADDING);
        cell.textContent = this.headers[i] || '';
        row.appendChild(cell);
      }
      head.appendChild(row);
      this.table.appendChild(head);
    }

    this.body = document.createElement('tbody');
    this.body.setAttribute('role', 'listbox');
    this.body.setAttribute('aria-labelledby', this.button.id);
    this.table.appendChild(this.body);

    this.scroller.appendChild(this.table);
    this.panel.appendChild(this.scroller);

    this.empty = document.createElement('div');
    this.empty.className = 'hidden px-3 py-3 text-sm text-gray-500 dark:text-gray-400';
    this.empty.textContent = 'No match';
    this.panel.appendChild(this.empty);

    this.root.appendChild(this.panel);
    this.options = Array.from(this.select.options);
    this.rows = [];
  }

  caret() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'h-4 w-4 shrink-0 text-gray-400');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute(
      'd',
      'M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'
    );
    path.setAttribute('clip-rule', 'evenodd');
    svg.appendChild(path);
    return svg;
  }

  /** The whole point: one column model shared by the header and every option. */
  colgroup() {
    const group = document.createElement('colgroup');
    for (let i = 0; i < this.columnCount; i++) {
      const col = document.createElement('col');
      if (this.widths[i]) col.style.width = this.widths[i];
      group.appendChild(col);
    }
    return group;
  }

  /** A table has no column-gap, so the gap lives in the cell padding. */
  padCell(cell, index, edge) {
    cell.style.paddingInline = `calc(${this.gap} / 2)`;
    if (index === 0) cell.style.paddingLeft = edge;
    if (index === this.columnCount - 1) cell.style.paddingRight = edge;
  }

  alignClass(index) {
    return ALIGN_CLASS[this.align[index]] || ALIGN_CLASS.left;
  }

  /** Only fixed columns have a width to truncate against. */
  clipClass() {
    return this.tableLayout === 'fixed' ? 'truncate' : 'whitespace-nowrap';
  }

  renderOptions() {
    this.body.textContent = '';
    this.options = Array.from(this.select.options);
    this.rowsBuilt = true;
    this.lastFilter = null;
    this.rows = this.options.map((option, index) => {
      const row = document.createElement('tr');
      row.className = ROW_CLASS;
      row.id = `${this.id}-option-${index}`;
      row.setAttribute('role', 'option');
      row.dataset.index = String(index);

      const values = readValues(option, this.separator, this.columnCount);
      const extra = split(option.dataset.classes, this.separator);
      values.forEach((value, i) => {
        const cell = document.createElement('td');
        cell.className =
          `${CELL_CLASS} ${this.alignClass(i)} ${this.clipClass()} ${extra[i] || ''}`.trim();
        this.padCell(cell, i, EDGE_PADDING);
        cell.textContent = value;
        row.appendChild(cell);
      });

      if (option.disabled) {
        row.classList.add('cursor-not-allowed', 'opacity-50');
      }
      row.dataset.search = values.join(' ').toLowerCase();
      this.body.appendChild(row);
      return row;
    });
  }

  /* ----------------------------------------------------------------- bind */

  bind() {
    this.button.addEventListener('click', () => this.toggle());
    this.button.addEventListener('keydown', (e) => this.onButtonKeydown(e));

    this.body.addEventListener('click', (e) => {
      const row = e.target.closest('[role="option"]');
      if (!row) return;
      const index = Number(row.dataset.index);
      if (this.options[index] && this.options[index].disabled) return;
      this.choose(index);
    });
    this.body.addEventListener('mousemove', (e) => {
      const row = e.target.closest('[role="option"]');
      if (!row) return;
      const index = Number(row.dataset.index);
      if (index === this.activeIndex) return;
      this.setActive(index, false);
    });

    if (this.search) {
      this.search.addEventListener('input', () => this.filter(this.search.value));
      this.search.addEventListener('keydown', (e) => this.onListKeydown(e));
    }
    this.panel.addEventListener('keydown', (e) => this.onListKeydown(e));

    this.onDocumentClick = (e) => {
      if (this.open && !this.root.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);

    // Keep the button in sync when other code sets select.value and fires
    // change, e.g. a filter reset.
    this.select.addEventListener('change', () => this.syncFromSelect());
  }

  /* ---------------------------------------------------------------- state */

  syncFromSelect() {
    const index = this.select.selectedIndex;
    this.buttonRow.textContent = '';

    if (index < 0 || !this.select.options[index]) {
      this.buttonRow.style.display = 'block';
      const cell = document.createElement('span');
      cell.className = 'block truncate text-gray-400 dark:text-gray-500';
      cell.textContent = this.placeholder;
      this.buttonRow.appendChild(cell);
    } else {
      // Same widths and layout mode as the panel, so the closed select shows
      // its values in the columns of the list they were picked from.
      this.buttonRow.style.display = 'table';
      this.buttonRow.style.width = '100%';
      this.buttonRow.style.tableLayout = this.tableLayout;

      const option = this.select.options[index];
      const values = readValues(option, this.separator, this.columnCount);
      const extra = split(option.dataset.classes, this.separator);
      values.forEach((value, i) => {
        const cell = document.createElement('span');
        cell.className = `${this.alignClass(i)} ${this.clipClass()} ${extra[i] || ''}`.trim();
        cell.style.display = 'table-cell';
        cell.style.verticalAlign = 'middle';
        if (this.widths[i]) cell.style.width = this.widths[i];
        // The button brings its own horizontal padding, so only the gap between
        // the columns is added here.
        this.padCell(cell, i, '0px');
        cell.textContent = value;
        this.buttonRow.appendChild(cell);
      });
    }

    if (this.rows.length) {
      this.rows.forEach((row, i) => {
        const selected = i === index;
        row.setAttribute('aria-selected', selected ? 'true' : 'false');
        row.classList.toggle('bg-blue-50', selected);
        row.classList.toggle('dark:bg-gray-700', selected);
        row.classList.toggle('font-medium', selected);
      });
    }
  }

  setActive(index, scroll = true) {
    if (index === this.activeIndex) {
      if (scroll && this.rows[index]) {
        this.rows[index].scrollIntoView({ block: 'nearest' });
      }
      return;
    }
    const previous = this.rows[this.activeIndex];
    if (previous) {
      previous.classList.remove('bg-gray-100', 'dark:bg-gray-600');
    }
    this.activeIndex = index;
    const row = this.rows[index];
    if (!row) return;
    row.classList.add('bg-gray-100', 'dark:bg-gray-600');
    this.body.setAttribute('aria-activedescendant', row.id);
    if (scroll) row.scrollIntoView({ block: 'nearest' });
  }

  /** Move the active row by `step`, skipping disabled and filtered rows. */
  move(step) {
    const usable = this.rows
      .map((row, i) => ({ row, i }))
      .filter(({ row, i }) => !row.classList.contains('hidden') && !this.options[i].disabled);
    if (!usable.length) return;

    const current = usable.findIndex(({ i }) => i === this.activeIndex);
    const next = current === -1 ? (step > 0 ? 0 : usable.length - 1) : current + step;
    const clamped = Math.max(0, Math.min(usable.length - 1, next));
    this.setActive(usable[clamped].i);
  }

  choose(index) {
    this.select.selectedIndex = index;
    this.syncFromSelect();
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
    this.button.focus();
  }

  filter(term) {
    const needle = term.trim().toLowerCase();
    if (needle === this.lastFilter) return;
    this.lastFilter = needle;
    let visible = 0;
    this.rows.forEach((row) => {
      const match = !needle || row.dataset.search.includes(needle);
      row.classList.toggle('hidden', !match);
      if (match) visible++;
    });
    this.empty.classList.toggle('hidden', visible > 0);
  }

  /* ----------------------------------------------------------- open/close */

  toggle() {
    this.open ? this.close() : this.openPanel();
  }

  openPanel() {
    if (this.select.disabled) return;
    if (!this.rowsBuilt) {
      this.renderOptions();
      this.syncFromSelect();
    }
    this.open = true;
    this.panel.classList.remove('hidden');
    this.button.setAttribute('aria-expanded', 'true');
    this.position();
    this.setActive(this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0);
    if (this.search) {
      if (this.search.value) {
        this.search.value = '';
        this.filter('');
      }
      this.search.focus();
    }
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.panel.classList.add('hidden');
    this.button.setAttribute('aria-expanded', 'false');
  }

  /** Drop below the button, flip above when there is not enough room. */
  position() {
    this.panel.classList.remove('bottom-full', 'mb-1', 'top-full', 'mt-1');
    const space = window.innerHeight - this.button.getBoundingClientRect().bottom;
    // Avoid measuring the full table; the scroller is capped at max-h-64.
    const estimated = (this.withSearch ? 52 : 0) + 256 + 16;
    const flip = space < estimated;
    this.panel.classList.add(...(flip ? ['bottom-full', 'mb-1'] : ['top-full', 'mt-1']));
  }

  /* -------------------------------------------------------------- keyboard */

  onButtonKeydown(e) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      this.openPanel();
      if (e.key === 'ArrowDown') this.move(1);
      if (e.key === 'ArrowUp') this.move(-1);
    }
  }

  onListKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        e.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        e.preventDefault();
        this.setActive(this.rows.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (this.activeIndex >= 0) this.choose(this.activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        this.button.focus();
        break;
      case 'Tab':
        this.close();
        break;
      default:
        if (!this.search && e.key.length === 1) this.onTypeahead(e.key);
    }
  }

  onTypeahead(char) {
    clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);

    const hit = this.rows.findIndex(
      (row, i) => !this.options[i].disabled && row.dataset.search.startsWith(this.typeahead)
    );
    if (hit >= 0) this.setActive(hit);
  }

  /** Re-read the native <select>, e.g. after options were replaced by HTMX. */
  refresh() {
    this.renderOptions();
    this.syncFromSelect();
  }

  destroy() {
    document.removeEventListener('click', this.onDocumentClick);
    this.select.classList.remove('sr-only');
    this.select.removeAttribute('tabindex');
    this.select.removeAttribute('aria-hidden');
    this.root.parentNode.insertBefore(this.select, this.root);
    this.root.remove();
    delete this.select._customSelect;
  }
}

/** Enhance every not-yet-enhanced .js-custom-select below `root`. */
export function initCustomSelects(root = document) {
  root.querySelectorAll(SELECTOR).forEach((select) => {
    if (select._customSelect) return;
    select._customSelect = new CustomSelect(select);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCustomSelects());
} else {
  initCustomSelects();
}

// Content swapped in by HTMX carries plain <select> markup, so enhance it too.
// The event bubbles, so listening on document also covers late-loaded partials.
document.addEventListener('htmx:afterSwap', (e) => initCustomSelects(e.target));

window.CustomSelect = { init: initCustomSelects };

export default CustomSelect;
